import { FileAudio, FileText, FileVideo, Image as ImageIcon, File } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { MEDIA_KIND_LABELS, type MediaKind } from "@/lib/media-library-filter";

export const mediaKindOf = (mimeType?: string | null): MediaKind => {
  if (!mimeType) return "other";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("application/") || mimeType.startsWith("text/")) return "document";
  return "other";
};

export const MEDIA_KIND_ICONS: Record<MediaKind, LucideIcon> = {
  image: ImageIcon,
  video: FileVideo,
  audio: FileAudio,
  document: FileText,
  other: File,
};

const UNITS = ["B", "KB", "MB", "GB", "TB"];

export const formatFileSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return "0 B";
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${UNITS[exponent]}`;
};

export const fileExtensionOf = (media: { file_name?: string; file_extension?: string }): string => {
  if (media.file_extension) return media.file_extension.toUpperCase();
  const parts = media.file_name?.split(".") ?? [];
  return parts.length > 1 ? (parts.pop() as string).toUpperCase() : "";
};

export const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds < 1) return "< 1s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest > 0 ? `${minutes}m ${rest}s` : `${minutes}m`;
};

/** Subtypes long enough to read as jargon rather than as a file type. */
const shortSubtype = (mimeType: string): string | null => {
  const subtype = (mimeType.split("/")[1] ?? "").replace(/^x-/, "");
  return subtype.length > 0 && subtype.length <= 5 ? subtype.toUpperCase() : null;
};

/** Renders a relation's `media_allowed_computed_mime_types` as field help text. */
export const describeAcceptedFiles = (allowedMimeTypes?: Array<string>): string => {
  const types = allowedMimeTypes?.filter(Boolean) ?? [];
  if (types.length === 0 || types.includes("application/octet-stream")) return "Any file type";

  const subtypes = types.map(shortSubtype);
  if (subtypes.every(Boolean) && new Set(subtypes).size <= 5) {
    return Array.from(new Set(subtypes as Array<string>)).join(", ");
  }

  const kinds = Array.from(new Set(types.map(mediaKindOf)));
  return kinds.map((kind) => MEDIA_KIND_LABELS[kind]).join(", ");
};
